'use client'

import { cn } from '@/ui/utils/style'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { forwardRef, useMemo } from 'react'

interface SliderProps {
  value: number[]
  onValueChange: (value: number[]) => void
  onValueCommit?: (value: number[]) => void
  onPointerDown?: (event: React.PointerEvent<HTMLSpanElement>) => void
  onPointerUp?: (event: React.PointerEvent<HTMLSpanElement>) => void
  onLostPointerCapture?: (event: React.PointerEvent<HTMLSpanElement>) => void
  onKeyUp?: (event: React.KeyboardEvent<HTMLSpanElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLSpanElement>) => void
  max?: number
  min?: number
  step?: number
  disabled?: boolean
  className?: string
  /** Custom className for the filled range track (overrides default gradient) */
  trackClassName?: string
  /**
   * When true, the trackClassName gradient is fixed across the full track width
   * and revealed as the slider value increases (instead of stretching with the value)
   */
  fixedGradient?: boolean
}

const Slider = forwardRef<HTMLSpanElement, SliderProps>(
  (
    {
      className,
      value,
      onValueChange,
      onValueCommit,
      onPointerDown,
      onPointerUp,
      onLostPointerCapture,
      onKeyUp,
      onBlur,
      max = 100,
      min = 0,
      step = 1,
      disabled = false,
      trackClassName,
      fixedGradient = false,
    },
    ref,
  ) => {
    // Calculate values for fixed gradient mode
    const valuePercent = useMemo(() => {
      const currentValue = value[0] ?? 0
      return ((currentValue - min) / (max - min)) * 100
    }, [value, min, max])

    // Background size to make gradient span full track width (not just Range width)
    // If Range is at 50%, background-size needs to be 200% to show full gradient
    // Clamp to 100% max so gradient never stretches beyond full width
    const backgroundSize = useMemo(() => {
      if (valuePercent <= 0) return '100%'
      // Clamp valuePercent to max 100% for the calculation
      const clampedPercent = Math.min(valuePercent, 100)
      return `${(100 / clampedPercent) * 100}%`
    }, [valuePercent])

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          'relative flex w-full touch-none select-none items-center',
          disabled && 'cursor-default opacity-50',
          className,
        )}
        value={value}
        onValueChange={onValueChange}
        onValueCommit={onValueCommit}
        max={max}
        min={min}
        step={step}
        disabled={disabled}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onLostPointerCapture={onLostPointerCapture}
        onKeyUp={onKeyUp}
        onBlur={onBlur}
      >
        <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-white/10">
          <SliderPrimitive.Range
            className={cn(
              'absolute h-full',
              trackClassName ?? 'bg-gradient-to-r from-gradient-yellow to-gradient-green',
            )}
            style={
              fixedGradient && trackClassName
                ? { backgroundSize: backgroundSize, backgroundPosition: 'left' }
                : undefined
            }
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className={cn(
            'block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'data-[disabled]:pointer-events-none disabled:pointer-events-none data-[disabled]:cursor-default disabled:opacity-50',
            'active:cursor-grabbing hover:cursor-grab hover:bg-accent',
          )}
        />
      </SliderPrimitive.Root>
    )
  },
)

Slider.displayName = 'Slider'

export { Slider }
export type { SliderProps }
